/**
 * HUD. Deliberately sparse — a technical riding game wants the screen clear.
 *
 * OWNER: piece:world-content (wave 5). Was piece:ui-hud.
 *
 * WHAT IS ON SCREEN, AND WHY EACH THING IS THERE
 * ---------------------------------------------------------------------------
 *  bottom-left   speed, traction bar, airtime      the bike
 *  top-left      location strip, two modes         the mountain
 *  world-space   up to four run/trailhead labels   where you are and what is over there
 *  centre        run result, on finishing a run    the reason to reach the bottom
 *  top-right     debug panel, off by default       not shipped
 *
 * That is the whole list, and the total painted area of the two riding elements is an
 * audited number (`content.hudScreenFrac`, budget 7% of the viewport) precisely so that
 * "minimal" is not a claim in a comment. `docs/BAR.md` asks for a clear screen; the budget
 * is how that survives the next person who wants to add a line.
 *
 * The top-left block has TWO modes and stays one block — see `StripMode`. R2's version had
 * only the on-a-run mode and simply vanished everywhere else, which left the product
 * silent for seconds at a time in ordinary riding.
 *
 * WHY THE RUN STRIP EXISTS AT ALL
 * ---------------------------------------------------------------------------
 * The wave-5 coherence pass found an entire content system the player cannot see:
 * `__gripTrails.network()` returns 20 named runs — Sunday Best, Sandblaster, Widowmaker,
 * Traverse 3 — with lengths, drops, grades, characters and 276 features between them,
 * and the player never learns any of it exists. The world spawn is 517 m down Sunday
 * Best and nothing on screen has ever said so.
 *
 * The strip is the answer to "what am I on". It is four short lines in the top-left
 * corner, in the identity palette (`docs/IDENTITY.md`: bone, one accent, hard edges, flat
 * fills, no glass and no glow), and it says the run's name, its grade, its character, its
 * length, and how much of it is behind you. `src/game/content.ts` computes all of that
 * from the trail network's own query API and writes it into `runStrip` below; this file
 * decides only how it looks.
 *
 * THE ONE PLACE THE TREAD MOTIF IS SPENT
 * ---------------------------------------------------------------------------
 * `docs/IDENTITY.md` offers the wordmark's knocked-out tread band as a UI device and
 * names progress fills specifically, with the instruction to use it "sparingly enough
 * that it stays a signature". It appears exactly once in the HUD: the progress fill on
 * the run strip. Nowhere else.
 *
 * COHERENCE PASS (wave 3) — kept, unchanged in substance
 * ---------------------------------------------------------------------------
 *  - The air timer used to be 34 px, dead centre, at 16% height, i.e. in the middle of
 *    the sky where the player reads the next thirty metres of trail. It is a line in the
 *    corner block, at the airtime the bar actually calls a jump.
 *  - The speed was a bare unlabelled number, which is what a frame counter looks like.
 *  - The traction bar ramped mint -> amber -> red, and nothing else in the game is mint.
 *    It runs bone -> the accent -> hot.
 *
 * COHERENCE PASS (wave 4) — kept
 * ---------------------------------------------------------------------------
 * The HUD must not contradict the sim. While `s.crashed` holds, the block recedes and
 * the traction bar releases: the speed is a body sliding on its side, and slip at the
 * limit is what a tire reads when it is no longer carrying anyone. The run strip and the
 * world labels recede with it, for the same reason.
 *
 * EVERY COLOUR HERE COMES FROM `src/render/palette.ts`
 * ---------------------------------------------------------------------------
 * The wave-4 pass found five palettes and no author. `Identity.bone`, `Identity.accent`
 * and `Identity.ink` are the identity layer's three values and they are imported, not
 * retyped. The one exception is the hot end of the traction bar, which is a signal
 * colour rather than a palette entry and is documented at its constant.
 */

import type { BikeState } from '../bike/physics';
import type { LoopStats } from '../core/loop';
import { Identity, srgbHex, oklch } from '../render/palette';

/** Left edge of the readout blocks, px. One number so the stacks cannot drift apart. */
const GUTTER = 34;

/** The identity layer's three values, from the palette authority. */
const BONE = srgbHex(Identity.bone);
const ACCENT = srgbHex(Identity.accent);
const INK = srgbHex(Identity.ink);

/**
 * Traction bar ladder. Bone until the tire starts working, the bike's own accent orange
 * as it approaches the limit, hot at the limit.
 *
 * GRIP_OK is bone dropped to 0.76 L: the bar at rest must not compete with the type
 * above it. GRIP_LIMIT is the one colour in this file that is not a palette entry — it
 * is a signal, not a material, and it is deliberately hotter and more saturated than
 * anything the palette permits in the world so that "past the limit" cannot be confused
 * with "near the limit".
 */
const GRIP_OK = srgbHex(oklch(0.76, 0.008, 248));
const GRIP_EDGE = ACCENT;
const GRIP_LIMIT = '#e0522f';

/**
 * Airtime past which the readout appears, s.
 *
 * 0.25 s was catching chatter: a wheel unweighting over a root is not a jump, and a
 * number that blinks on and off twice a second is noise wherever it sits. docs/BAR.md
 * puts a real hit at 1.2-2.5 s, so 0.4 is comfortably below anything intentional and
 * comfortably above skipping.
 */
const AIR_SHOW = 0.4;

/**
 * Opacity of the readout blocks while the sim says the rider is down.
 *
 * Low enough to read as "this is not live", high enough that the block does not appear to
 * have been switched off — a HUD element that vanishes reads as a bug, one that recedes
 * reads as a state. It comes back the instant `crashed` clears, which is the same frame
 * the bike is remounted and pointed back down the hill.
 */
const DOWN_OPACITY = '0.34';

/** Width of the run strip's progress rule, px. The strip's whole block is this wide. */
const STRIP_W = 208;

/**
 * The identity's ink, as an rgba() plate at a given alpha.
 *
 * `Identity.ink` is the measured foreground value of the key art (`docs/IDENTITY.md`:
 * R13 G24 B44, a dark desaturated blue-black, NOT plum). Every dark field in the menus is
 * this value, so a HUD plate and a menu plate are the same colour and the product reads
 * as one thing.
 */
function plate(alpha: number): string {
  const rgb = Identity.ink;
  const c = (v: number): number => Math.round(
    Math.max(0, Math.min(1, v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055)) * 255,
  );
  return `rgba(${c(rgb[0])},${c(rgb[1])},${c(rgb[2])},${alpha})`;
}

/** The identity layer's type stacks. Same two as `src/ui/menus.ts`, deliberately. */
const FACE_DISPLAY = "'Arial Black','Helvetica Neue',Helvetica,Arial,system-ui,sans-serif";
const FACE_LABEL = 'ui-monospace,SFMono-Regular,Menlo,monospace';

// ---------------------------------------------------------------------------
// Difficulty blazes
//
// The international MTB grammar, and it is worth using rather than inventing: a green
// circle, a blue square, a black diamond, two black diamonds. A rider reads those before
// they read a word, and they are the same four symbols the world signage in
// `src/game/content.ts` paints onto its boards — one vocabulary in two places.
//
// The symbol always sits on a bone plate, as it does on a real sign. That is not
// decoration: a black diamond on a dark HUD is invisible, and putting the plate behind it
// is how the real object solves the same problem.
// ---------------------------------------------------------------------------

export type BlazeGrade = 'green' | 'blue' | 'black' | 'double-black' | '';

/** Symbol fills. Muted toward the palette rather than pure signage colours: this sits
 *  next to the world, and a pure #00ff00 circle would be the only thing in the frame
 *  above the world chroma ceiling. */
const BLAZE_FILL: Record<string, string> = {
  green: srgbHex(oklch(0.55, 0.088, 148)),
  blue: srgbHex(oklch(0.50, 0.086, 244)),
  black: srgbHex(oklch(0.17, 0.010, 250)),
  'double-black': srgbHex(oklch(0.17, 0.010, 250)),
};

/** Short word for the grade, as it appears in the strip's meta line. */
const BLAZE_WORD: Record<string, string> = {
  green: 'GREEN',
  blue: 'BLUE',
  black: 'BLACK',
  'double-black': 'DOUBLE BLACK',
};

/**
 * Build a blaze chip: a bone plate carrying the grade's symbol.
 *
 * `size` is the plate's edge in px. Returns the plate; call `setBlaze` to (re)paint it.
 */
function makeBlaze(size: number): HTMLDivElement {
  const plate = document.createElement('div');
  plate.style.cssText =
    `width:${size}px;height:${size}px;background:${BONE};flex:0 0 auto;`
    + 'display:flex;align-items:center;justify-content:center;gap:1px;';
  for (let i = 0; i < 2; i++) {
    const sym = document.createElement('div');
    sym.style.cssText = 'flex:0 0 auto;';
    plate.appendChild(sym);
  }
  return plate;
}

/** Repaint a blaze chip for a grade. Touches the DOM only when the grade changed. */
function setBlaze(plate: HTMLDivElement, grade: string, size: number): void {
  if (plate.dataset.grade === grade) return;
  plate.dataset.grade = grade;
  const fill = BLAZE_FILL[grade] ?? BLAZE_FILL.blue;
  const two = grade === 'double-black';
  const a = plate.children[0] as HTMLDivElement;
  const b = plate.children[1] as HTMLDivElement;
  // A diamond is a square turned 45deg, so it needs ~0.7 of the space a square does to
  // occupy the same plate. Two diamonds need to be smaller again or they touch the edges.
  const s = grade === 'green' ? size * 0.60
    : grade === 'blue' ? size * 0.56
      : two ? size * 0.34 : size * 0.46;
  const shape = grade === 'green' ? '50%' : '0';
  const rot = grade === 'black' || two ? 'rotate(45deg)' : 'none';
  a.style.cssText =
    `width:${s.toFixed(1)}px;height:${s.toFixed(1)}px;background:${fill};`
    + `border-radius:${shape};transform:${rot};flex:0 0 auto;`;
  b.style.cssText = two ? a.style.cssText : 'display:none;';
}

// ---------------------------------------------------------------------------
// The contract with src/game/content.ts
//
// Plain mutable module state rather than a callback or an event: `content`'s update runs
// inside `updateSystems()`, which main.ts calls immediately before `hud.update()`, so a
// write here is always read in the same frame. It also keeps main.ts untouched, which
// matters with nine agents in the tree.
//
// Nothing in here allocates. The pools are fixed size and the strings are assigned, not
// built, unless a value actually changed.
// ---------------------------------------------------------------------------

/**
 * Which of the strip's two states is up.
 *
 * ROUND 3 — THE STRIP HAS AN OFF-RUN STATE, AND THAT IS THE WHOLE CARRIED GAP.
 *
 * R2 shipped a strip that came up on a named run and went down everywhere else, so the
 * moment the rider left the tread the product said nothing at all: no name, no elevation,
 * no way back. Measured on the shipped build with `captures/w5-content-tools/
 * offrun-probe.mjs`, replaying `vista`'s own input script:
 *
 *     t=4.05s .. t=7.05s   3.0 s at 57-64 km/h   strip DOWN, HUD text ""   0 chips
 *                          nearest named run 21.8 m -> 2.5 m away, unnamed
 *
 * `off` is not a degraded `run`. It answers a different question — not "how much of this
 * run is left" but "where am I, what is the nearest line, and which way is it" — and it
 * reuses the same four nodes so the corner does not grow by a pixel.
 */
export type StripMode = '' | 'run' | 'off';

/** What the top-left strip says about where the rider is. */
export interface RunStripState {
  /**
   * `run` — on a named run: name, grade, character, length, progress, distance to go.
   * `off` — not on one: the state, the rider's elevation, the NEAREST named run and how
   *         far and which way its tread is.
   * `''`  — nothing to say (no network), strip down.
   */
  mode: StripMode;
  /** The run being ridden, or in `off` mode the nearest named run. */
  name: string;
  difficulty: BlazeGrade;
  /** `flow` | `tech` | `loose` | `link`, from the trail network. */
  character: string;
  lengthM: number;
  // --- `run` mode ------------------------------------------------------------------
  /** 0..1 along the run's centreline. */
  progress: number;
  /** Metres of run still ahead. */
  toGoM: number;
  /** True while the rider is on the easier line around a feature. */
  onBLine: boolean;
  // --- `off` mode ------------------------------------------------------------------
  /** Metres from the bike to the nearest point on `name`'s centreline. */
  distM: number;
  /** Radians to that point in the CAMERA's frame: 0 straight ahead, +ve to the right. */
  bearing: number;
  /** The rider's elevation, m. The one number that says where you are on a mountain. */
  elevM: number;
}

export const runStrip: RunStripState = {
  mode: '',
  name: '',
  difficulty: '',
  character: '',
  lengthM: 0,
  progress: 0,
  toGoM: 0,
  onBLine: false,
  distM: 0,
  bearing: 0,
  elevM: 0,
};

/**
 * Metres from a centreline under which the rider counts as standing on the tread.
 *
 * Shared with `src/game/content.ts`, which uses it to decide the header word, so the two
 * files cannot disagree about what "off trail" means.
 */
export const TREAD_NEAR_M = 3.0;

/** Most world labels that may be on screen at once. See `content.poiOnScreen`. */
export const MAX_POI = 4;

/**
 * One projected world label. `content` fills the screen position — it owns the camera,
 * the frustum test and the terrain occlusion march — and this file only positions a node.
 */
export interface PoiLabel {
  visible: boolean;
  /** Viewport px, top-left origin, of the anchor point on the ground. */
  sx: number;
  sy: number;
  /** 0..1. Distance fade, and the ramp in and out. */
  alpha: number;
  label: string;
  /** Second line: distance, drop, whatever identifies it. */
  sub: string;
  difficulty: BlazeGrade;
}

export const poiLabels: PoiLabel[] = Array.from({ length: MAX_POI }, () => ({
  visible: false, sx: 0, sy: 0, alpha: 0, label: '', sub: '', difficulty: '' as BlazeGrade,
}));

/** Everything the result card shows. All of it measured over the run just ridden. */
export interface RunResultCard {
  name: string;
  difficulty: BlazeGrade;
  character: string;
  timeS: number;
  lengthM: number;
  dropM: number;
  topSpeedKmh: number;
  avgSpeedKmh: number;
  airS: number;
  bestAirS: number;
  features: number;
  crashes: number;
  cleanFeatures: number;
  /** Previous best for this run this session, or null when it is the first descent. */
  bestTimeS: number | null;
  isBest: boolean;
}

// ---------------------------------------------------------------------------
// The Hud instance. main.ts constructs exactly one; the module-level functions below
// route to it. `live` is null only before construction, which no caller can observe.
// ---------------------------------------------------------------------------

let live: Hud | null = null;

/** Show the result card. Idempotent for the same run; replaces it otherwise. */
export function showRunResult(card: RunResultCard): void {
  live?.showResult(card);
}

/** Take the result card down. */
export function hideRunResult(): void {
  live?.hideResult();
}

/** True while the result card is on screen. */
export function runResultVisible(): boolean {
  return live ? live.resultOn : false;
}

/** Everything `hudChromeMetrics()` reads back off the glass. */
export interface ChromeMetrics {
  /** Text of the run-name node, or '' when the strip is not painted. */
  stripName: string;
  /** Text of the `off`-mode state line — '' whenever that line is not painted, which is
   *  how a caller tells `off` from `run` without being handed this class's own state. */
  stripKind: string;
  /** Text of the bottom line: distance to go, or the way back to the tread. */
  stripFoot: string;
  stripPx: number;
  stripOpacity: number;
  poiPx: number;
  poiCount: number;
  totalPx: number;
  resultOn: boolean;
}

/**
 * What content's own chrome is actually painting, measured off the live DOM.
 *
 * This is the instrument behind `content.runNamedOnScreen` and `content.hudScreenFrac`,
 * and it is deliberately not a copy of what `content` believes it asked for: `stripName`
 * is read back out of the text node, and `stripPx` is a real `getBoundingClientRect`. A
 * strip that is in the DOM with `display:none`, zero width, or somebody else's name in it
 * reports honestly here and would report perfectly if the metric read `runStrip` instead.
 * That distinction is the entire difference between this and the wave-4 `overTravel` that
 * was structurally blind to the thing it was watching.
 *
 * Allocating (one object) and it does layout. Call it at a few Hz, not per frame.
 */
export function hudChromeMetrics(): ChromeMetrics {
  if (!live) {
    return {
      stripName: '', stripKind: '', stripFoot: '', stripPx: 0, stripOpacity: 0,
      poiPx: 0, poiCount: 0, totalPx: 0, resultOn: false,
    };
  }
  return live.chromeMetrics();
}

export class Hud {
  private readonly root: HTMLDivElement;
  private readonly speedEl: HTMLDivElement;
  private readonly speedUnitEl: HTMLSpanElement;
  private readonly speedValEl: HTMLSpanElement;
  private readonly gripEl: HTMLDivElement;
  private readonly airEl: HTMLDivElement;
  private readonly debugEl: HTMLPreElement;

  // --- run strip -------------------------------------------------------------------
  private readonly stripEl: HTMLDivElement;
  private readonly stripKindEl: HTMLDivElement;
  private readonly stripBlaze: HTMLDivElement;
  private readonly stripNameEl: HTMLDivElement;
  private readonly stripMetaEl: HTMLDivElement;
  private readonly stripTrackEl: HTMLDivElement;
  private readonly stripFillEl: HTMLDivElement;
  private readonly stripArrowEl: HTMLDivElement;
  private readonly stripToGoEl: HTMLDivElement;
  private stripShown = false;
  private stripMode: StripMode = '';
  private lastName = '';
  private lastKind = '';
  private lastMeta = '';
  private lastToGo = '';
  private lastFillPct = -1;
  private lastArrowDeg = 999;

  // --- world labels ----------------------------------------------------------------
  private readonly poiEls: {
    root: HTMLDivElement; blaze: HTMLDivElement; label: HTMLDivElement; sub: HTMLDivElement;
    shown: boolean; lastLabel: string; lastSub: string;
  }[] = [];

  // --- result ----------------------------------------------------------------------
  private readonly resultEl: HTMLDivElement;
  private readonly resultBody: HTMLDivElement;
  /** Seconds the card stays up before it takes itself down. */
  private resultTimer = 0;
  resultOn = false;

  /** Last `crashed` the blocks were styled for, so the DOM is touched on transitions. */
  private wasDown = false;
  /** Cached lookup of the menu root, so the per-frame check is a property read. */
  private menuRoot: Element | null | undefined = undefined;
  debugVisible = false;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.style.cssText = [
      'position:fixed', 'inset:0', 'pointer-events:none',
      `font-family:${FACE_LABEL}`,
      `color:${BONE}`, 'text-shadow:0 1px 3px rgba(0,0,0,.65)',
    ].join(';');

    this.speedEl = document.createElement('div');
    this.speedEl.style.cssText =
      `position:absolute;left:${GUTTER}px;bottom:30px;font-size:52px;font-weight:600;`
      + 'letter-spacing:-2px;line-height:1;transition:opacity 140ms linear;';
    this.speedValEl = document.createElement('span');
    // The unit rides with the number rather than sitting under it, so the block keeps
    // one baseline and the digits can change width without anything below moving.
    this.speedUnitEl = document.createElement('span');
    this.speedUnitEl.textContent = ' km/h';
    this.speedUnitEl.style.cssText =
      'font-size:15px;font-weight:500;letter-spacing:0;opacity:.62;';
    this.speedEl.appendChild(this.speedValEl);
    this.speedEl.appendChild(this.speedUnitEl);
    this.root.appendChild(this.speedEl);

    this.gripEl = document.createElement('div');
    this.gripEl.style.cssText =
      `position:absolute;left:${GUTTER}px;bottom:16px;width:170px;height:4px;`
      + 'background:rgba(255,255,255,.14);overflow:hidden;'
      + 'transition:opacity 140ms linear;';
    const bar = document.createElement('div');
    bar.style.cssText = `height:100%;width:0%;background:${GRIP_OK};transition:none;`;
    this.gripEl.appendChild(bar);
    this.root.appendChild(this.gripEl);

    // Sits directly above the speed, in the same corner block. It used to be 34 px in
    // the middle of the sky, over the horizon line the player reads the trail from.
    this.airEl = document.createElement('div');
    this.airEl.style.cssText =
      `position:absolute;left:${GUTTER}px;bottom:92px;font-size:19px;font-weight:600;`
      + 'letter-spacing:0;line-height:1;opacity:0;transition:opacity 90ms linear;';
    this.root.appendChild(this.airEl);

    // --- run identity strip ---------------------------------------------------------
    this.stripEl = document.createElement('div');
    // Stable selectors for the falsification probe in `captures/w5-content-tools/`: it
    // hides these nodes on purpose and asserts the audit invariant notices. An instrument
    // nobody can break is an instrument nobody has tested.
    this.stripEl.dataset.grip = 'run-strip';
    // A flat ink plate with a hard accent edge, and it is not decoration.
    //
    // R1 shipped the strip as bare bone type with a drop shadow, and the capture said no:
    // over the pale sky at the top of the frame the meta line and the distance were
    // unreadable at 1280x720 — bone on bone, and a shadow cannot rescue 10 px type on a
    // near-white field. `docs/IDENTITY.md` already owns the fix and it is the same device
    // the menus use: a flat ground, hard edges, one accent, no blur and no glass. The
    // shadow comes off inside the plate, because a plate that needs a shadow is a panel
    // pretending to float.
    this.stripEl.style.cssText =
      `position:absolute;left:${GUTTER}px;top:26px;width:${STRIP_W}px;`
      + `background:${plate(0.62)};border-left:2px solid ${ACCENT};`
      + 'padding:9px 11px 10px;text-shadow:none;'
      + 'opacity:0;transition:opacity 180ms linear;';

    // The state line, and it exists only in `off` mode. It is the accent's one job in this
    // block: the rider is somewhere other than on a run, and the first thing they want is
    // not a name but "which of the two things am I looking at, and how high am I".
    this.stripKindEl = document.createElement('div');
    this.stripKindEl.dataset.grip = 'strip-kind';
    this.stripKindEl.style.cssText =
      `font-size:9.5px;letter-spacing:.28em;color:${ACCENT};text-transform:uppercase;`
      + 'line-height:1;margin-bottom:7px;white-space:nowrap;display:none;';
    this.stripEl.appendChild(this.stripKindEl);

    const nameRow = document.createElement('div');
    nameRow.style.cssText = 'display:flex;align-items:center;gap:8px;';
    this.stripBlaze = makeBlaze(15);
    nameRow.appendChild(this.stripBlaze);
    this.stripNameEl = document.createElement('div');
    this.stripNameEl.style.cssText =
      `font-family:${FACE_DISPLAY};font-size:17px;font-weight:900;letter-spacing:.035em;`
      + 'text-transform:uppercase;line-height:1.05;white-space:nowrap;';
    nameRow.appendChild(this.stripNameEl);
    this.stripEl.appendChild(nameRow);

    this.stripMetaEl = document.createElement('div');
    this.stripMetaEl.style.cssText =
      'font-size:10px;letter-spacing:.18em;text-transform:uppercase;opacity:.78;'
      + 'margin:6px 0 8px;white-space:nowrap;';
    this.stripEl.appendChild(this.stripMetaEl);

    const track = document.createElement('div');
    track.style.cssText =
      `width:${STRIP_W}px;height:3px;background:rgba(232,238,244,.16);overflow:hidden;`;
    this.stripTrackEl = track;
    this.stripFillEl = document.createElement('div');
    // The tread motif, spent once. A flat accent base with the wordmark's knocked-out
    // diagonal band repeated through it — the same device, at HUD scale, rising left to
    // right exactly as it does on the mark.
    this.stripFillEl.style.cssText =
      `height:100%;width:0%;background-color:${ACCENT};`
      + 'background-image:repeating-linear-gradient(115deg,'
      + 'rgba(13,24,44,.55) 0 2px,rgba(13,24,44,0) 2px 6px);';
    track.appendChild(this.stripFillEl);
    this.stripEl.appendChild(track);

    // The bottom line: in `run` mode the distance still to ride, in `off` mode the way
    // back. Both are right-aligned against the same rule, so the block does not reflow
    // when the mode changes.
    const foot = document.createElement('div');
    foot.style.cssText =
      `margin-top:6px;width:${STRIP_W}px;display:flex;align-items:center;`
      + 'justify-content:flex-end;gap:7px;';
    // The bearing arrow. A flat accent triangle drawn in borders — no glyph, because a
    // font arrow would be at the mercy of whatever the browser substitutes, and no image.
    // Zero degrees points UP the screen, which is where the camera is already looking, so
    // the rotation reads directly as "turn that way".
    this.stripArrowEl = document.createElement('div');
    this.stripArrowEl.dataset.grip = 'strip-arrow';
    this.stripArrowEl.style.cssText =
      'width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;'
      + `border-bottom:9px solid ${ACCENT};flex:0 0 auto;display:none;`;
    foot.appendChild(this.stripArrowEl);
    this.stripToGoEl = document.createElement('div');
    this.stripToGoEl.dataset.grip = 'strip-foot';
    this.stripToGoEl.style.cssText =
      'font-size:10px;letter-spacing:.16em;text-transform:uppercase;opacity:.72;'
      + 'text-align:right;white-space:nowrap;';
    foot.appendChild(this.stripToGoEl);
    this.stripEl.appendChild(foot);
    this.root.appendChild(this.stripEl);

    // --- world labels ---------------------------------------------------------------
    for (let i = 0; i < MAX_POI; i++) {
      const el = document.createElement('div');
      el.dataset.grip = 'poi';
      // Anchored bottom-centre on the world point, so the label sits above the thing it
      // names and the tick lands on it.
      el.style.cssText =
        'position:absolute;left:0;top:0;transform:translate(-50%,-100%);opacity:0;'
        + 'display:flex;flex-direction:column;align-items:center;white-space:nowrap;';
      // The chip. Same flat ink plate as the run strip, for the same measured reason: a
      // label naming a ridge is drawn against sky and snow more often than not, and bone
      // type on either is a smudge. Hard edges, no radius, no blur.
      const chip = document.createElement('div');
      chip.style.cssText =
        `background:${plate(0.66)};padding:5px 8px 6px;text-shadow:none;`
        + 'display:flex;flex-direction:column;align-items:flex-start;';
      const text = document.createElement('div');
      text.style.cssText = 'display:flex;align-items:center;gap:6px;';
      const blaze = makeBlaze(10);
      const label = document.createElement('div');
      label.style.cssText =
        `font-family:${FACE_DISPLAY};font-size:12px;font-weight:900;letter-spacing:.05em;`
        + 'text-transform:uppercase;line-height:1;';
      text.appendChild(blaze);
      text.appendChild(label);
      chip.appendChild(text);
      const sub = document.createElement('div');
      sub.style.cssText =
        'font-size:9.5px;letter-spacing:.16em;text-transform:uppercase;opacity:.72;'
        + 'margin-top:4px;line-height:1;';
      chip.appendChild(sub);
      el.appendChild(chip);
      // The tick: 1 px of bone from the type down to the ground point. It is what makes
      // the label point at a place rather than float in the sky.
      const tick = document.createElement('div');
      tick.style.cssText = `width:1px;height:14px;background:${BONE};opacity:.42;margin-top:4px;`;
      el.appendChild(tick);
      this.root.appendChild(el);
      this.poiEls.push({
        root: el, blaze, label, sub, shown: false, lastLabel: '', lastSub: '',
      });
    }

    // --- result card ----------------------------------------------------------------
    this.resultEl = document.createElement('div');
    this.resultEl.dataset.grip = 'result';
    this.resultEl.style.cssText =
      'position:absolute;inset:0;display:none;align-items:center;justify-content:center;';
    this.resultBody = document.createElement('div');
    this.resultBody.style.cssText =
      `background:${INK};color:${BONE};width:min(560px,84vw);padding:0 0 22px;`
      + `border-top:3px solid ${ACCENT};text-shadow:none;`;
    this.resultEl.appendChild(this.resultBody);
    this.root.appendChild(this.resultEl);

    this.debugEl = document.createElement('pre');
    this.debugEl.style.cssText =
      'position:absolute;right:16px;top:16px;font-size:11px;line-height:1.45;margin:0;opacity:.82;display:none;';
    this.root.appendChild(this.debugEl);

    parent.appendChild(this.root);
    live = this;

    window.addEventListener('keydown', (e) => {
      if (e.code === 'Backquote') {
        this.debugVisible = !this.debugVisible;
        this.debugEl.style.display = this.debugVisible ? 'block' : 'none';
      }
      // Any key clears a result that is still up: it is a celebration, not a modal, and
      // it must never be between the player and the next run.
      if (this.resultOn) this.hideResult();
    });
  }

  update(s: BikeState, stats: LoopStats): void {
    const kmh = s.speed * 3.6;
    this.speedValEl.textContent = kmh.toFixed(0);

    // See the coherence-pass note in the header. While the sim holds `crashed` the rider
    // is on the floor: the speed is a slide, not a ride, and slip is what a tire reads
    // when it has stopped carrying anyone. Recede rather than assert.
    if (s.crashed !== this.wasDown) {
      this.wasDown = s.crashed;
      const o = s.crashed ? DOWN_OPACITY : '1';
      this.speedEl.style.opacity = o;
      this.gripEl.style.opacity = o;
    }

    // The bar is released while down rather than dimmed with the block, because a
    // dimmed-but-pinned bar still reads as "at the limit" — the one thing it must not say
    // about a bike nobody is riding.
    const slip = s.crashed ? 0 : Math.max(s.front.slipUsage, s.rear.slipUsage);
    const bar = this.gripEl.firstElementChild as HTMLDivElement;
    bar.style.width = `${Math.min(100, slip * 100).toFixed(1)}%`;
    bar.style.background = slip > 0.95 ? GRIP_LIMIT : slip > 0.8 ? GRIP_EDGE : GRIP_OK;

    if (!s.crashed && s.airborne && s.airTime > AIR_SHOW) {
      this.airEl.style.opacity = '0.9';
      this.airEl.textContent = `AIR ${s.airTime.toFixed(1)}s`;
    } else {
      this.airEl.style.opacity = '0';
    }

    this.updateStrip(s);
    this.updatePois(s);
    this.updateResult(stats);

    if (this.debugVisible) {
      this.debugEl.textContent = [
        `fps    ${stats.fps.toFixed(0)}  sim ${stats.simMs.toFixed(2)}ms  gfx ${stats.renderMs.toFixed(2)}ms`,
        `steps  ${stats.stepsLastFrame}`,
        `speed  ${s.speed.toFixed(2)} m/s`,
        `lean   ${(s.lean * 57.3).toFixed(1)}deg  cmd ${(s.leanCommand * 57.3).toFixed(1)}`,
        `pitch  ${(s.pitch * 57.3).toFixed(1)}deg  yaw ${(s.yaw * 57.3).toFixed(1)}`,
        `steer  ${(s.steer * 57.3).toFixed(1)}deg`,
        `susp   F ${(s.front.travel * 1000).toFixed(0)}mm  R ${(s.rear.travel * 1000).toFixed(0)}mm`,
        `load   F ${s.front.load.toFixed(0)}N  R ${s.rear.load.toFixed(0)}N`,
        `slip   F ${s.front.slipUsage.toFixed(2)}  R ${s.rear.slipUsage.toFixed(2)}`,
        `spin   F ${s.front.spin.toFixed(1)}  R ${s.rear.spin.toFixed(1)}`,
        `air    ${s.airborne ? s.airTime.toFixed(2) : '-'}  g ${s.gForce.toFixed(2)}`,
        `pos    ${s.x.toFixed(1)} ${s.y.toFixed(1)} ${s.z.toFixed(1)}`,
        `run    ${runStrip.mode === 'run' ? `${runStrip.name} ${(runStrip.progress * 100).toFixed(0)}%`
          : runStrip.mode === 'off' ? `off, ${runStrip.name} ${runStrip.distM.toFixed(0)}m` : '-'}`,
      ].join('\n');
    }
  }

  /** True while a menu screen owns the frame. The HUD stands aside for it entirely. */
  private menuOpen(): boolean {
    if (this.menuRoot === undefined) this.menuRoot = document.querySelector('.gm-root');
    return this.menuRoot ? this.menuRoot.getAttribute('data-open') === '1' : false;
  }

  private updateStrip(s: BikeState): void {
    const mode = runStrip.mode;
    const want = mode !== '' && !this.menuOpen();
    if (want !== this.stripShown) {
      this.stripShown = want;
      // Put the fade back after `showResult` cut it. See the note there.
      if (want && this.stripEl.style.transition === 'none') {
        this.stripEl.style.transition = 'opacity 180ms linear';
      }
      this.stripEl.style.opacity = want ? '1' : '0';
    }
    if (!want) return;

    // Mode switch: three display flags, touched only on the transition. The nodes are the
    // same in both modes — the state line and the progress rule are what differ.
    if (mode !== this.stripMode) {
      this.stripMode = mode;
      const off = mode === 'off';
      this.stripKindEl.style.display = off ? 'block' : 'none';
      this.stripTrackEl.style.display = off ? 'none' : 'block';
      this.stripArrowEl.style.display = off ? 'block' : 'none';
    }

    if (runStrip.name !== this.lastName) {
      this.lastName = runStrip.name;
      this.stripNameEl.textContent = runStrip.name;
    }
    setBlaze(this.stripBlaze, runStrip.difficulty, 15);

    // One string, rebuilt only when a field it depends on moves. `toFixed` allocates, so
    // the guard is not cosmetic — this runs every frame.
    const km = runStrip.lengthM / 1000;
    const meta = `${BLAZE_WORD[runStrip.difficulty] ?? ''} · ${runStrip.character.toUpperCase()} `
      + `· ${km >= 1 ? `${km.toFixed(2)} KM` : `${runStrip.lengthM.toFixed(0)} M`}`
      + (mode === 'run' && runStrip.onBLine ? ' · B-LINE' : '');
    if (meta !== this.lastMeta) {
      this.lastMeta = meta;
      this.stripMetaEl.textContent = meta;
    }

    if (mode === 'run') {
      if (this.lastKind !== '') { this.lastKind = ''; this.stripKindEl.textContent = ''; }
      const pct = Math.round(Math.min(1, Math.max(0, runStrip.progress)) * 1000) / 10;
      if (pct !== this.lastFillPct) {
        this.lastFillPct = pct;
        this.stripFillEl.style.width = `${pct}%`;
      }
      const toGoKm = runStrip.toGoM / 1000;
      const toGo = runStrip.toGoM < 30 ? 'FINISH'
        : toGoKm >= 1 ? `${toGoKm.toFixed(2)} KM TO GO`
          : `${runStrip.toGoM.toFixed(0)} M TO GO`;
      if (toGo !== this.lastToGo) {
        this.lastToGo = toGo;
        this.stripToGoEl.textContent = toGo;
      }
    } else {
      // The rider's own elevation rides in the state line rather than in the footer,
      // because "OFF TRAIL · 748 M" cannot be misread and "22 M TO TREAD · 748 M" can.
      const on = runStrip.distM <= TREAD_NEAR_M;
      const kind = `${on ? 'ON TRAIL' : 'OFF TRAIL'} · ${runStrip.elevM.toFixed(0)} M`;
      if (kind !== this.lastKind) {
        this.lastKind = kind;
        this.stripKindEl.textContent = kind;
      }
      const foot = on ? 'ON THE TREAD'
        : runStrip.distM >= 1000 ? `${(runStrip.distM / 1000).toFixed(2)} KM TO TREAD`
          : `${runStrip.distM.toFixed(0)} M TO TREAD`;
      if (foot !== this.lastToGo) {
        this.lastToGo = foot;
        this.stripToGoEl.textContent = foot;
      }
      // Quantised to 3 degrees: the arrow must not shimmer while the camera settles, and
      // 3 degrees is well inside the width of the triangle itself.
      const deg = on ? 999
        : Math.round((runStrip.bearing * 180) / Math.PI / 3) * 3;
      if (deg !== this.lastArrowDeg) {
        this.lastArrowDeg = deg;
        this.stripArrowEl.style.display = on ? 'none' : 'block';
        if (!on) this.stripArrowEl.style.transform = `rotate(${deg}deg)`;
      }
    }

    // Recede with the rest of the block while the rider is down.
    this.stripEl.style.opacity = s.crashed ? DOWN_OPACITY : '1';
  }

  private updatePois(s: BikeState): void {
    const dim = s.crashed ? 0.34 : 1;
    const hide = this.menuOpen() || this.resultOn;
    for (let i = 0; i < this.poiEls.length; i++) {
      const p = poiLabels[i];
      const e = this.poiEls[i];
      const on = p.visible && !hide;
      if (!on) {
        if (e.shown) {
          e.shown = false;
          e.root.style.opacity = '0';
          // Parked off-screen as well as transparent: a transparent node still takes part
          // in hit testing and, more importantly, still shows up in a bounding-rect audit.
          e.root.style.transform = 'translate(-50%,-100%) translate(-9999px,-9999px)';
        }
        continue;
      }
      e.shown = true;
      if (p.label !== e.lastLabel) { e.lastLabel = p.label; e.label.textContent = p.label; }
      if (p.sub !== e.lastSub) { e.lastSub = p.sub; e.sub.textContent = p.sub; }
      setBlaze(e.blaze, p.difficulty, 10);
      e.root.style.transform =
        `translate(-50%,-100%) translate(${p.sx.toFixed(1)}px,${p.sy.toFixed(1)}px)`;
      e.root.style.opacity = (p.alpha * dim).toFixed(3);
    }
  }

  // -------------------------------------------------------------------------------
  // Result card
  //
  // `docs/IDENTITY.md`: results are an identity surface. Warm ground, high contrast, hard
  // edges, no soft shadows, no glassy panels. So: the measured foreground value of the key
  // art as the plate, one accent rule at the top, and the numbers set in the identity's
  // two faces.
  //
  // The WORDMARK is deliberately NOT on this card. It would want its 180 px minimum plus a
  // cap-height of clear space on all four sides, which a 560 px card cannot give it, and
  // `docs/IDENTITY.md` lists "a badge/plate variant for challenge results" as an OPEN
  // QUESTION FOR BRETT. Inventing one is precisely what that section says not to do.
  //
  // It is built as HTML in one write rather than forty nodes because it appears once at
  // the end of a run and is not a hot path.
  // -------------------------------------------------------------------------------

  showResult(card: RunResultCard): void {
    const t = (sec: number): string => {
      const m = Math.floor(sec / 60);
      const r = sec - m * 60;
      return `${m}:${r < 10 ? '0' : ''}${r.toFixed(1)}`;
    };
    const km = card.lengthM / 1000;
    const stat = (v: string, k: string): string =>
      `<div style="flex:1 1 0;min-width:0"><div style="font-family:${FACE_DISPLAY};`
      + `font-size:25px;font-weight:900;letter-spacing:-.01em;line-height:1">${v}</div>`
      + `<div style="font-size:9.5px;letter-spacing:.2em;opacity:.5;margin-top:5px">${k}</div></div>`;
    const delta = card.bestTimeS === null ? ''
      : card.isBest
        ? `<span style="color:${ACCENT}">NEW BEST −${t(card.bestTimeS - card.timeS)}</span>`
        : `+${t(card.timeS - card.bestTimeS)} OFF BEST`;

    this.resultBody.innerHTML =
      `<div style="padding:20px 26px 0">`
      + `<div style="font-size:10px;letter-spacing:.32em;color:${ACCENT}">RUN COMPLETE</div>`
      + `<div style="display:flex;align-items:center;gap:11px;margin:11px 0 3px">`
      + `<div data-blaze="1"></div>`
      + `<div style="font-family:${FACE_DISPLAY};font-size:40px;font-weight:900;`
      + `letter-spacing:.01em;line-height:1;text-transform:uppercase">${card.name}</div></div>`
      + `<div style="font-size:10px;letter-spacing:.2em;opacity:.55;text-transform:uppercase">`
      + `${BLAZE_WORD[card.difficulty] ?? ''} · ${card.character} · `
      + `${km >= 1 ? `${km.toFixed(2)} km` : `${card.lengthM.toFixed(0)} m`} · `
      + `${card.dropM.toFixed(0)} m drop</div>`
      + `<div style="height:1px;background:rgba(232,238,244,.22);margin:18px 0 16px"></div>`
      + `<div style="display:flex;gap:14px">`
      + stat(t(card.timeS), 'TIME')
      + stat(card.topSpeedKmh.toFixed(0), 'TOP KM/H')
      + stat(card.avgSpeedKmh.toFixed(0), 'AVG KM/H')
      + stat(card.airS.toFixed(1), 'AIR S')
      + `</div>`
      + `<div style="display:flex;gap:14px;margin-top:16px">`
      + stat(`${card.cleanFeatures}/${card.features}`, 'FEATURES CLEAN')
      + stat(card.bestAirS.toFixed(1), 'BIGGEST AIR S')
      + stat(String(card.crashes), 'CRASHES')
      + `</div>`
      + `<div style="font-size:10.5px;letter-spacing:.2em;margin-top:18px;min-height:13px">`
      + `${delta}</div>`
      + `</div>`;
    const slot = this.resultBody.querySelector('[data-blaze="1"]') as HTMLDivElement | null;
    if (slot) {
      const b = makeBlaze(26);
      setBlaze(b, card.difficulty, 26);
      slot.replaceWith(b);
    }
    this.resultEl.style.display = 'flex';
    this.resultOn = true;
    this.resultTimer = RESULT_HOLD_S;
    // The strip goes at once rather than over its 180 ms fade. It says "612 M TO GO" about
    // a run that has just been finished, and a card that announces RUN COMPLETE with that
    // still under it contradicts itself — the wave-4 lesson about the HUD asserting things
    // the frame disproves, in a new place.
    //
    // R2: that was the intent and the code did not deliver it. Setting `opacity` alone
    // still goes through the element's own `transition: opacity 180ms`, so the strip
    // faded over the card for a fifth of a second and the comment above was describing
    // something that did not happen — `captures/w5-content-gallery/result_card.png`
    // caught it: RUN COMPLETE over a live "1.40 KM TO GO". The transition is cut for this
    // one change and restored in `updateStrip` when the strip next comes up.
    this.stripShown = false;
    this.stripEl.style.transition = 'none';
    this.stripEl.style.opacity = '0';
  }

  hideResult(): void {
    if (!this.resultOn) return;
    this.resultOn = false;
    this.resultTimer = 0;
    this.resultEl.style.display = 'none';
  }

  private updateResult(stats: LoopStats): void {
    if (!this.resultOn) return;
    // Wall-clock-independent: the harness steps frames faster than real time and a
    // setTimeout would take the card down after two frames of a stepped capture.
    this.resultTimer -= Math.min(0.1, stats.frameMs / 1000);
    if (this.resultTimer <= 0) this.hideResult();
  }

  chromeMetrics(): ChromeMetrics {
    // Read back out of the text node, not out of `runStrip`, and only after the node has
    // been shown to occupy real pixels inside the viewport. See the note on
    // `hudChromeMetrics`.
    //
    // The first draft asked only for opacity, and the probe that exists to falsify this
    // instrument found the hole immediately: `display:none` on the strip left the text
    // node full and the inline opacity at 1, so the metric reported the run name as
    // visible while the screen showed nothing. Area and on-screen-ness are the test;
    // opacity is only one of the ways it can be zero.
    const sr = this.stripEl.getBoundingClientRect();
    const stripOpacity = this.stripShown ? Number(this.stripEl.style.opacity || '0') : 0;
    const onGlass = sr.width > 0 && sr.height > 0
      && sr.right > 0 && sr.bottom > 0
      && sr.left < window.innerWidth && sr.top < window.innerHeight;
    const painted = this.stripShown && stripOpacity > 0.05 && onGlass;
    const stripName = painted ? (this.stripNameEl.textContent ?? '') : '';
    const stripPx = painted ? sr.width * sr.height : 0;
    // The state line and the footer, read the same way and for the same reason: the mode
    // the strip is IN has to be recoverable from what is painted, not from what this class
    // believes it asked for. `stripKind` is empty both when the node is display:none (its
    // rect collapses) and when the strip as a whole is not painted, so a caller can tell
    // `run` from `off` from `nothing` without being told.
    const kr = this.stripKindEl.getBoundingClientRect();
    const stripKind = painted && kr.width > 0 && kr.height > 0
      ? (this.stripKindEl.textContent ?? '') : '';
    const stripFoot = painted ? (this.stripToGoEl.textContent ?? '') : '';
    let poiPx = 0;
    let poiCount = 0;
    for (const e of this.poiEls) {
      if (!e.shown) continue;
      const a = Number(e.root.style.opacity || '0');
      if (a <= 0.05) continue;
      const r = e.root.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      if (r.right < 0 || r.bottom < 0 || r.left > window.innerWidth || r.top > window.innerHeight) continue;
      poiPx += r.width * r.height;
      poiCount++;
    }
    return {
      stripName,
      stripKind,
      stripFoot,
      stripPx,
      stripOpacity,
      poiPx,
      poiCount,
      totalPx: stripPx + poiPx,
      resultOn: this.resultOn,
    };
  }
}

/**
 * Seconds the result card holds before taking itself down.
 *
 * Long enough to read eleven numbers without hurrying, short enough that a rider who has
 * already pushed off is not looking through it. Any key also clears it.
 */
const RESULT_HOLD_S = 9;
